import { inject, Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { AuthorizationDetail, ICaseDTO, IDocumentResponseDTO, Line, ServiceLineSummaryDTO } from "@core/model";
import { ProductNames } from "@mynexus/mynexus-ui-lib";
import { DocumentsService } from "./document-utility.service";
import { SelectItem } from "primeng/api";

@Injectable({
  providedIn: 'root',
})
export class DocumentService {
  /* 
    Services/Injections
  */
  private documentUtilityService = inject(DocumentsService);

  /* 
    Class property fields
  */
  private documents: BehaviorSubject<IDocumentResponseDTO[]> = new BehaviorSubject([]);
  private sourceFilters = new BehaviorSubject<SelectItem[]>([]);
  private typeFilters = new BehaviorSubject<SelectItem[]>([]);
  private createdByFilters = new BehaviorSubject<SelectItem[]>([]);
  private originalDocuments: IDocumentResponseDTO[] = [];

  // Read-only access to those pesky consumers
  public documents$ = this.documents.asObservable();
  public sourceFilters$ = this.sourceFilters.asObservable();
  public typeFilters$ = this.typeFilters.asObservable();
  public createdByFilters$ = this.createdByFilters.asObservable();

  /* 
    Public methods
  */
  /** Combines documents into a single DocumentResponse per Filename, and sets that updated collection in the Service's document$ subscription. */
  public combineAndSetDocuments(rawDocuments: IDocumentResponseDTO[], collectionOfLines: unknown[], program: string) {
    this.originalDocuments = rawDocuments;
    const lines:Map<string, Line> = this.convertLines(collectionOfLines, program);
    const docMap = new Map<string, IDocumentResponseDTO>();
    // Initialize Sets to track unique values
    const sourcesSet = new Set<string>();
    const typesSet = new Set<string>();
    const createdBySet = new Set<string>();

    rawDocuments.forEach(doc => {
      // map the lineNumbers into the doc, using the AuthDetailId
      const matchingLine = lines.get(doc.authorizationDetailId);
      if (matchingLine) {
        // Set the doc's lineNumbers to the Line's lineNumber property
        doc.lineNumbers = matchingLine.lineNumber.toString() || '';
      }
      
      // Standardize key to handle potential casing inconsistencies
      const key = doc.documentFileName.toLowerCase();
      // Here we use the Map to consolidate the documents, using the key we just set
      if (!docMap.has(key)) {
        // First time seeing this file, add it to the map
        docMap.set(key, {
          ...doc,
          dateTimeFaxReceived: this.documentUtilityService.utcToLocal(doc.dateTimeFaxReceived ?? doc.createdOn)
        });
      } 
      else {
        const existing = docMap.get(key);
        
        // Only append the type if it's not already in the string (prevents: "Approval Letter, Approval Letter")
        if (!existing.documentType.includes(doc.documentType)) {
          existing.documentType += `, ${doc.documentType}`;
        }
        
        // Combine unique Line Numbers
        if (doc.lineNumbers && !existing.lineNumbers?.includes(doc.lineNumbers)) {
          existing.lineNumbers += `, ${doc.lineNumbers}`;
        }

        // TODO? Potentially combine the AuthDetailIDs as well, for better lookup later?
        // After this loop, the resulting element's AuthDetailId can NO LONGER be trusted, as it now represents multiple records.
        // Potential refactor idea: rip the IDocumentResponseDTO[] out, and use a FE-only object to represent the combined lines instead.
      }

      // Add the filter options here
      if (doc.documentSource) sourcesSet.add(doc.documentSource);
      if (doc.documentType) typesSet.add(doc.documentType);
      if (doc.createdBy) createdBySet.add(doc.createdBy);
    });

    const sortedDocs = Array.from(docMap.values()).sort((a, b) => {
      const dateA = new Date(a.dateTimeFaxReceived).getTime();
      const dateB = new Date(b.dateTimeFaxReceived).getTime();
      return dateB - dateA;
    });

    this.documents.next(sortedDocs);
    this.sourceFilters.next(this.mapToSortedSelectItems(sourcesSet));
    this.typeFilters.next(this.mapToSortedSelectItems(typesSet));
    this.createdByFilters.next(this.mapToSortedSelectItems(createdBySet));
  }

  /*   
    Gets the current combined documents synchronously.
  */
  public getMasterDocuments(): IDocumentResponseDTO[] {
    return this.documents.value;
  }

  /**
   * Gets all original uncombined document records for a given filename.
   */
  public getOriginalDocumentsByFilename(filename: string): IDocumentResponseDTO[] {
    return this.originalDocuments.filter(doc => 
      doc.documentFileName.toLowerCase() === filename.toLowerCase()
    );
  }

  /**
   * Gets all original uncombined documents by their metadata IDs.
   */
  public getOriginalDocumentsByIds(docMetaDataIds: string[]): IDocumentResponseDTO[] {
    // First, get the filenames of the selected combined documents
    const selectedCombinedDocs = this.documents.value.filter(doc => 
      docMetaDataIds.includes(doc.docMetaDataId)
    );
    
    // Then get all original records for those filenames
    const allOriginalRecords: IDocumentResponseDTO[] = [];
    selectedCombinedDocs.forEach(combinedDoc => {
      const originalRecords = this.getOriginalDocumentsByFilename(combinedDoc.documentFileName);
      allOriginalRecords.push(...originalRecords);
    });
    
    return allOriginalRecords;
  }

  /* 
    Private methods
  */
  /** Used to convert Program Specific "Lines" into a generic Line model, returns a Map for O(1) lookup.*/
  private convertLines(collection: unknown[], program: string): Map<string, Line> {
    let lines: Line[] = [];

    if (this.isCase(collection, program)) {
      lines = collection.flatMap(c => 
        c.productLines.map(x => ({
          lineId: x.productLineId,
          lineNumber: x.sequenceNumber
        }))
      );
    }
    else if (this.isAuthDetails(collection, program)) {
      lines = collection.map(x => ({
        lineId: x.authorizationDetailId,
        lineNumber: x.sequenceNumber
      }));
    }
    else if (this.isServiceLine(collection, program)) {
      lines = collection.map(x => ({
        lineId: x.serviceLineId,
        lineNumber: x.serviceNumber
      }));
    }

    return new Map(lines.map(line => [line.lineId, line]));
  }

  // The Type Guards, lets us cast better and safer
  private isCase(data: unknown[], program: string): data is ICaseDTO[] {
    return program === ProductNames.DMEPOS;
  }

  private isAuthDetails(data: unknown[], program: string): data is AuthorizationDetail[] {
    return program === ProductNames.HomeHealth;
  }

  // TODO: Remove PAC Commercial after LOB migration
  private isServiceLine(data: unknown[], program: string): data is ServiceLineSummaryDTO[] {
    return program === ProductNames.PACIM || program === ProductNames.PacCommercial;
  }

  // Current usage is all string here, but leave open for future use.
  private mapToSortedSelectItems(set: Set<string | number>): SelectItem[] {
    return Array.from(set)
      .sort((a, b) => {
        return (a < b) ? -1 : (a > b) ? 1 : 0; // should handle both (string | number)
      })
      .map(x => ({ value: x, label: x?.toString() }));
  }
}